import React, { useState, useEffect } from "react";
import { User, Upload } from "lucide-react";
import axios from "axios";

const MainOnboardingForm = () => {
  const [personalInfo, setPersonalInfo] = useState({
    lastName: "",
    firstName: "",
    middleName: "",
    mobileNo: "",
    alternativeNo: "",
    emailId: "",
    alternativeEmail: "",
    dob: "",
    gender: "",
    maritalStatus: "",
    whatsAppNo: "",
    telegramId: "",
    linkedinId: "",
  });

  const [stemInfo, setStemInfo] = useState({
    visaType: "",
    visaStartDate: "",
    visaEndDate: "",
    uscisNo: "",
    cardNo: "",
    sevisSchoolCode: "",
    fieldOfStudy: "",
    stemId: "",
    dsoName: "",
    dsoNumber: "",
    dsoEmail: "",
  });

  const [generalInfo, setGeneralInfo] = useState({
    currentAddress: "",
    emergencyContactName: "",
    emergencyContactNumber: "",
    emergencyContactEmail: "",
    relation: "",
  });

  const [payrollInfo, setPayrollInfo] = useState({
    ssn: "",
    address: "",
    bankName: "",
    bankAddress: "",
    accountNumber: "",
    routingNumber: "",
    accountType: "",
  });

  const [documentInfo, setDocumentInfo] = useState({});
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [requestInvoiceId, setRequestInvoiceId] = useState("");

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const leadIdParam = urlParams.get("leadID");
    const requestInvoiceIdParam = urlParams.get("requestInvoiceId");
    const selectedDocsParam = urlParams.get("selectedDocuments");

    setLeadId(leadIdParam || "");
    setRequestInvoiceId(requestInvoiceIdParam || "");

    if (selectedDocsParam) {
      try {
        const docs = JSON.parse(decodeURIComponent(selectedDocsParam));
        setSelectedDocuments(docs);

        // Initialize document info state
        const initialDocInfo = {};
        docs.forEach((doc) => {
          initialDocInfo[doc] = null; // Will store file objects
        });
        setDocumentInfo(initialDocInfo);
      } catch (error) {
        console.error("Error parsing selected documents:", error);
      }
    }
  }, []);

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleStemInfoChange = (field, value) => {
    setStemInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleGeneralInfoChange = (field, value) => {
    setGeneralInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handlePayrollInfoChange = (field, value) => {
    setPayrollInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleDocumentChange = (docName, file) => {
    setDocumentInfo((prev) => ({ ...prev, [docName]: file }));
  };

  const handleSubmit = async () => {
    // Create FormData to handle both JSON data and files
    const formData = new FormData();

    // Complete JSON payload with ALL fields from the form
    const jsonPayload = {
      // Basic Info
      firstName: personalInfo.firstName,
      middleName: personalInfo.middleName,
      lastName: personalInfo.lastName,
      fullName: `${personalInfo.firstName} ${personalInfo.middleName} ${personalInfo.lastName}`,
      phoneNumber: personalInfo.mobileNo,
      email: personalInfo.emailId,
      whatsappNumber: personalInfo.whatsAppNo,
      telegramId: personalInfo.telegramId,
      linkedInProfile: personalInfo.linkedinId,
      gender: personalInfo.gender,
      maritalStatus: personalInfo.maritalStatus,

      // Emergency Contact
      emergencyContact: {
        contactNumber: generalInfo.emergencyContactNumber,
        relation: generalInfo.relation,
        alternativeNumber: personalInfo.alternativeNo,
        alternativeEmail: personalInfo.alternativeEmail,
      },

      // Personal Info
      dateOfBirth: personalInfo.dob,
      ssn: payrollInfo.ssn,

      // Visa Info (STEM Section)
      currentVisaStatus: stemInfo.visaType,
      visaStartDate: stemInfo.visaStartDate,
      visaExpiryDate: stemInfo.visaEndDate,
      cardNumber: stemInfo.cardNo,
      uscisNumber: stemInfo.uscisNo,
      sevisSchoolCode: stemInfo.sevisSchoolCode,
      stemEmploymentStartDate: null, // You may provide this if you have
      dsoInfo: {
        name: stemInfo.dsoName,
        phone: stemInfo.dsoNumber,
        email: stemInfo.dsoEmail,
      },

      // Home Address
      homeAddress: generalInfo.currentAddress || {
        street: null,
        city: null,
        state: null,
        zipCode: null,
        country: null,
      },

      // Bank / Compensation Info
      compensation: {
        bankDetails: {
          name: payrollInfo.bankName,
          address: payrollInfo.bankAddress || {
            street: null,
            city: null,
            state: null,
            zipCode: null,
          },
          accountNumber: payrollInfo.accountNumber,
          routingNumber: payrollInfo.routingNumber,
          accountType: payrollInfo.accountType,
        },
      },

      // Education
      education: [
        {
          university: "Example University",
          yearOfPassing: "2024",
          degree: "Bachelor's",
          fieldOfStudy: "Computer Science",
          gpa: 3.5,
        },
      ],
    };

    // Append JSON data
    formData.append("data", JSON.stringify(jsonPayload));

    // Append document files (keeping existing document handling)
    Object.keys(documentInfo).forEach((docName) => {
      if (documentInfo[docName]) {
        formData.append(`documents[${docName}]`, documentInfo[docName]);
      }
    });

    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/onboarding/leads/${leadId}/onboarding?requestInvoiceId=${requestInvoiceId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      alert("Onboarding data saved successfully!");
    } catch (error) {
      alert("Error saving onboarding data. See console for details.");
      console.error(error);
    }
  };

  return (
    <div className="w-full mx-auto p-6 bg-white">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Employee Onboarding Form
        </h1>
        <p className="text-gray-600">
          Please fill out all required information for onboarding process
        </p>
      </div>

      <div className="space-y-8">
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-2">
            Personal Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                value={personalInfo.firstName}
                onChange={(e) =>
                  handlePersonalInfoChange("firstName", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Middle Name
              </label>
              <input
                type="text"
                value={personalInfo.middleName}
                onChange={(e) =>
                  handlePersonalInfoChange("middleName", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                value={personalInfo.lastName}
                onChange={(e) =>
                  handlePersonalInfoChange("lastName", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile No *
              </label>
              <input
                type="tel"
                value={personalInfo.mobileNo}
                onChange={(e) =>
                  handlePersonalInfoChange("mobileNo", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alternative No
              </label>
              <input
                type="tel"
                value={personalInfo.alternativeNo}
                onChange={(e) =>
                  handlePersonalInfoChange("alternativeNo", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email ID *
              </label>
              <input
                type="email"
                value={personalInfo.emailId}
                onChange={(e) =>
                  handlePersonalInfoChange("emailId", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alternative Email
              </label>
              <input
                type="email"
                value={personalInfo.alternativeEmail}
                onChange={(e) =>
                  handlePersonalInfoChange("alternativeEmail", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                DOB *
              </label>
              <input
                type="date"
                value={personalInfo.dob}
                onChange={(e) =>
                  handlePersonalInfoChange("dob", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender *
              </label>
              <select
                value={personalInfo.gender}
                onChange={(e) =>
                  handlePersonalInfoChange("gender", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marital Status
              </label>
              <select
                value={personalInfo.maritalStatus}
                onChange={(e) =>
                  handlePersonalInfoChange("maritalStatus", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Status</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp No
              </label>
              <input
                type="tel"
                value={personalInfo.whatsAppNo}
                onChange={(e) =>
                  handlePersonalInfoChange("whatsAppNo", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telegram ID
              </label>
              <input
                type="text"
                value={personalInfo.telegramId}
                onChange={(e) =>
                  handlePersonalInfoChange("telegramId", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LinkedIn ID
              </label>
              <input
                type="text"
                value={personalInfo.linkedinId}
                onChange={(e) =>
                  handlePersonalInfoChange("linkedinId", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* STEM Information Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-2">
            STEM Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visa Type
              </label>
              <input
                type="text"
                value={stemInfo.visaType}
                onChange={(e) =>
                  handleStemInfoChange("visaType", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visa Start Date
              </label>
              <input
                type="date"
                value={stemInfo.visaStartDate}
                onChange={(e) =>
                  handleStemInfoChange("visaStartDate", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visa End Date
              </label>
              <input
                type="date"
                value={stemInfo.visaEndDate}
                onChange={(e) =>
                  handleStemInfoChange("visaEndDate", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                USCIS NO
              </label>
              <input
                type="text"
                value={stemInfo.uscisNo}
                onChange={(e) =>
                  handleStemInfoChange("uscisNo", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Card NO
              </label>
              <input
                type="text"
                value={stemInfo.cardNo}
                onChange={(e) => handleStemInfoChange("cardNo", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SEVIS School Code
              </label>
              <input
                type="text"
                value={stemInfo.sevisSchoolCode}
                onChange={(e) =>
                  handleStemInfoChange("sevisSchoolCode", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Field Of Study (CIP Name & Code)
              </label>
              <input
                type="text"
                value={stemInfo.fieldOfStudy}
                onChange={(e) =>
                  handleStemInfoChange("fieldOfStudy", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                STEM ID
              </label>
              <input
                type="text"
                value={stemInfo.stemId}
                onChange={(e) => handleStemInfoChange("stemId", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                DSO Name
              </label>
              <input
                type="text"
                value={stemInfo.dsoName}
                onChange={(e) =>
                  handleStemInfoChange("dsoName", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                DSO Number
              </label>
              <input
                type="tel"
                value={stemInfo.dsoNumber}
                onChange={(e) =>
                  handleStemInfoChange("dsoNumber", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                DSO Email
              </label>
              <input
                type="email"
                value={stemInfo.dsoEmail}
                onChange={(e) =>
                  handleStemInfoChange("dsoEmail", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* General Information Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-2">
            General Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Address (Street, City, State, Zipcode)
              </label>
              <textarea
                value={generalInfo.currentAddress}
                onChange={(e) =>
                  handleGeneralInfoChange("currentAddress", e.target.value)
                }
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Name
                </label>
                <input
                  type="text"
                  value={generalInfo.emergencyContactName}
                  onChange={(e) =>
                    handleGeneralInfoChange(
                      "emergencyContactName",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Number
                </label>
                <input
                  type="tel"
                  value={generalInfo.emergencyContactNumber}
                  onChange={(e) =>
                    handleGeneralInfoChange(
                      "emergencyContactNumber",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Email
                </label>
                <input
                  type="email"
                  value={generalInfo.emergencyContactEmail}
                  onChange={(e) =>
                    handleGeneralInfoChange(
                      "emergencyContactEmail",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relation
                </label>
                <input
                  type="text"
                  value={generalInfo.relation}
                  onChange={(e) =>
                    handleGeneralInfoChange("relation", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Payroll Information Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-2">
            Payroll Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SSN
              </label>
              <input
                type="text"
                value={payrollInfo.ssn}
                onChange={(e) => handlePayrollInfoChange("ssn", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength="11"
                placeholder="XXX-XX-XXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                value={payrollInfo.address}
                onChange={(e) =>
                  handlePayrollInfoChange("address", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name
              </label>
              <input
                type="text"
                value={payrollInfo.bankName}
                onChange={(e) =>
                  handlePayrollInfoChange("bankName", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Address
              </label>
              <input
                type="text"
                value={payrollInfo.bankAddress}
                onChange={(e) =>
                  handlePayrollInfoChange("bankAddress", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Number
              </label>
              <input
                type="text"
                value={payrollInfo.accountNumber}
                onChange={(e) =>
                  handlePayrollInfoChange("accountNumber", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Routing Number
              </label>
              <input
                type="text"
                value={payrollInfo.routingNumber}
                onChange={(e) =>
                  handlePayrollInfoChange("routingNumber", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength="9"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Type
              </label>
              <select
                value={payrollInfo.accountType}
                onChange={(e) =>
                  handlePayrollInfoChange("accountType", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Account Type</option>
                <option value="Checking">Checking</option>
                <option value="Savings">Savings</option>
              </select>
            </div>
          </div>
        </div>

        {/* Document Upload Section */}
        {selectedDocuments.length > 0 && (
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-2">
              Document Upload
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedDocuments.map((docName, index) => (
                <div key={index} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {docName} *
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        handleDocumentChange(docName, file);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    />
                    <Upload className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  {documentInfo[docName] && (
                    <p className="text-sm text-green-600">
                      ✓ {documentInfo[docName].name}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end mt-8">
        <button
          onClick={handleSubmit}
          className="px-6 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default MainOnboardingForm;
